import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { Tooltip } from 'reactstrap';
import Icon from 'utility/icomoon';

interface FilterItem {
  icon: string;
  title: string;
  text: string[];
}

interface Props {
  items: FilterItem[];
}

const FilterBar: React.FC<Props> = ({ items }) => {
  const [tooltipOpens, setTooltipOpens] = useState<boolean[]>(items.map(() => false));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Función para verificar si hay overflow y actualizar estado de botones
  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      // Hay overflow si el contenido es más ancho que el contenedor
      const hasOverflow = scrollWidth > clientWidth;
      setShowScrollButtons(hasOverflow);

      // Verificar si se puede hacer scroll a la izquierda o derecha
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  // Effect para verificar overflow cuando cambien los items o se monte el componente
  useEffect(() => {
    checkScrollability();

    // Agregar listener para resize de ventana
    const handleResize = () => {
      setTimeout(checkScrollability, 100); // Pequeño delay para que termine el resize
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [items]);

  // Effect para verificar scrollability después de que se renderice el contenido
  useEffect(() => {
    const timer = setTimeout(checkScrollability, 50);
    return () => clearTimeout(timer);
  }, [items]);

  const toggleTooltip = (index: number) => {
    setTooltipOpens((prev) => prev.map((open, i) => (i === index ? !open : open)));
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth'
      });

      // Verificar scrollability después del scroll
      setTimeout(checkScrollability, 300);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth'
      });

      // Verificar scrollability después del scroll
      setTimeout(checkScrollability, 300);
    }
  };

  const handleScrollButtonClick = (e: React.MouseEvent, direction: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();

    if (direction === 'left') {
      scrollLeft();
    } else {
      scrollRight();
    }
  };

  const handleFilterItemClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Aquí puedes agregar la lógica para manejar el click en el item del filtro
  };

  // Handler para el evento de scroll manual
  const handleScroll = () => {
    checkScrollability();
  };

  return (
    <>
      <div className="filter-bar-container">
        {/* Botón izquierdo - Solo se muestra si hay overflow y se puede hacer scroll a la izquierda */}
        {showScrollButtons && canScrollLeft && (
          <button
            className="filter-scroll-button filter-scroll-left"
            onClick={(e) => handleScrollButtonClick(e, 'left')}
            aria-label="Scroll left"
          >
            <ChevronLeft size={14} />
          </button>
        )}

        <div ref={scrollContainerRef} className="filter-bar" onScroll={handleScroll}>
          {items.map((item, index) => (
            <div
              key={index}
              id={`breadcrumb-${index}`}
              className="filter-item"
              onMouseEnter={() => toggleTooltip(index)}
              onMouseLeave={() => toggleTooltip(index)}
              onClick={handleFilterItemClick}
            >
              <span className="filter-item-catalog">{item.title}</span>
              {item.text.map((textItem, i) => (
                <div key={i} style={{display:"flex",gap:"1px",}}>
                  <span className="filter-item-divisor">-</span>
                  <span className={`filter-item-value ${i === item.text.length - 1 ? 'last' : ''}`}>
                    {textItem}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Botón derecho - Solo se muestra si hay overflow y se puede hacer scroll a la derecha */}
        {showScrollButtons && canScrollRight && (
          <button
            className="filter-scroll-button filter-scroll-right"
            onClick={(e) => handleScrollButtonClick(e, 'right')}
            aria-label="Scroll right"
          >
            <ChevronRight size={14} />
          </button>
        )}
      </div>
    </>
  );
};

export default FilterBar;
